<template>
  <div class="col-12 col-lg-9">
    <div v-if="panel === 'loading'" class="py-5 text-center">
      <span class="spinner-border" role="status" aria-hidden="true"></span>
    </div>

    <!-- Could not load — never fall through to the empty state here, or a
         paying subscriber is told they have no subscription. -->
    <div v-else-if="panel === 'error'" class="py-3">
      <h1 class="display-5 mb-3"><b>Your Subscription</b></h1>
      <div class="alert alert-danger" role="alert">{{ loadErrorMessage }}</div>
      <button type="button" class="btn btn-outline-secondary" @click="load">Try again</button>
    </div>

    <!-- No active subscription -->
    <div v-else-if="panel === 'empty'" class="py-3">
      <h1 class="display-5 mb-3"><b>Your Subscription</b></h1>
      <div class="card">
        <div class="card-body d-flex align-items-center gap-3">
          <img :src="freeImage" class="purchases__thumb rounded" alt="Free plan" />
          <div>
            <h2 class="h4 mb-2"><b>You don't have an active subscription!</b></h2>
            <a :href="purchasesPath" class="text-brand-yellow d-inline-flex align-items-center">
              Check out our latest offers!
              <span class="material-icons ms-1">arrow_right_alt</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Manage / resume / pause / cancel -->
    <div v-else class="py-3">
      <template v-if="view === 'manage'">
        <ManageScreen
          v-if="display"
          :display="display"
          @update-payment="onUpdatePayment(0)"
          @resume="view = 'resume'"
          @pause="openPause(0)"
          @cancel="openCancel(0)"
        />

        <!-- Everything else active on the account. Holding more than one is
             normal: the legacy Android products grant both PRO and PRO +, and
             every multi-year offer, gift, ambassador award, group seat and
             Hillwalk tour arrives as a separate promotional grant. Showing only
             one of them is what hid a customer's 5 years of access behind the
             cancelled Play Store sub it had replaced. -->
        <section v-if="secondaryDisplays.length" class="purchases__also">
          <h2 class="h5 mb-1"><b>Also on your account</b></h2>
          <p class="text-muted purchases__also-hint">
            More than one thing is giving you PRO+ right now.
          </p>
          <SecondarySubscriptionCard
            v-for="(secondary, index) in secondaryDisplays"
            :key="secondary.key"
            :display="secondary"
            class="mb-3"
            @update-payment="onUpdatePayment(index + 1)"
            @cancel="openCancel(index + 1)"
          />
        </section>
      </template>

      <PausePicker v-else-if="view === 'pause'" @close="view = 'manage'" @done="onFlowDone" />
      <ResumePicker
        v-else-if="view === 'resume'"
        :charges-immediately="display?.resumeChargesImmediately ?? false"
        :access-ends-on="display?.accessEndsOn ?? null"
        @close="view = 'manage'"
        @done="onFlowDone"
      />
      <CancelFlow
        v-else-if="view === 'cancel'"
        :source="flowTarget?.source ?? 'other'"
        :pill-state="flowTarget?.pillState ?? 'active'"
        :expiration-date="flowTarget?.expirationDate ?? null"
        :management-url="storeManagementUrlFor(flowTarget)"
        @close="view = 'manage'"
        @done="onFlowDone"
      />
    </div>

    <StoreRedirectModal
      v-if="paymentRedirectBody"
      :body-text="paymentRedirectBody"
      :confirm-label="paymentNeedsSupport ? 'Contact support' : 'Go'"
      @cancel="closePaymentRedirect"
      @go="confirmPaymentRedirect"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Purchases } from "@revenuecat/purchases-js";
import graphClient from "@/utils/graphql/graph_client";
import { ActiveWebSubscriptionPrice, SubscriptionPauseState } from "@/utils/graphql/subscriptions";
import CancelFlow from "./components/cancel/CancelFlow.vue";
import ManageScreen from "./components/ManageScreen.vue";
import PausePicker from "./components/PausePicker.vue";
import ResumePicker from "./components/ResumePicker.vue";
import SecondarySubscriptionCard from "./components/SecondarySubscriptionCard.vue";
import StoreRedirectModal from "./components/StoreRedirectModal.vue";
import {
  buildDisplayModels,
  buildFallbackDisplayModel,
  entitlementsFromCustomerInfo,
  manageAction,
  pauseStateFromResult,
  priceStringFromResult,
  storeManagementUrl as resolveStoreManagementUrl,
  subscriptionPanel,
  updatePaymentPrompt,
  SUBSCRIPTION_LOAD_FAILURE_MESSAGE,
  type DisplayModel,
} from "./subscriptionLogic";
import { errorMessage, GENERIC_ERROR_MESSAGE } from "./errorMessage";
import { contactSupport } from "./support";
import FreeImage from "../../images/FREE-thumbnail.png";

type View = "manage" | "pause" | "resume" | "cancel";

const loading = ref(true);
const view = ref<View>("manage");
const displays = ref<DisplayModel[]>([]);
// Which card the open pause/cancel flow belongs to. A flow started from a
// secondary card must act on that subscription, not on the primary one.
const flowIndex = ref(0);
const managementURL = ref<string | null>(null);
const paymentRedirectBody = ref("");
const paymentNeedsSupport = ref(false);
const loadFailed = ref(false);
const loadErrorMessage = ref(SUBSCRIPTION_LOAD_FAILURE_MESSAGE);

const display = computed<DisplayModel | null>(() => displays.value[0] ?? null);
const secondaryDisplays = computed<DisplayModel[]>(() => displays.value.slice(1));
const flowTarget = computed<DisplayModel | null>(() => displays.value[flowIndex.value] ?? null);

const panel = computed(() =>
  subscriptionPanel({
    loading: loading.value,
    loadFailed: loadFailed.value,
    hasSubscription: displays.value.length > 0,
  }),
);

const freeImage = FreeImage;
const firebaseUserId: string = window.user?.firebase_user_id;

const purchasesPath = computed(() => `${window.location.origin}/purchases`);

function storeManagementUrlFor(target: DisplayModel | null): string | null {
  if (!target) return null;
  return resolveStoreManagementUrl(target.source, managementURL.value);
}

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    const purchases = Purchases.configure(window.rcPublicKey, firebaseUserId);
    const customerInfo: any = await purchases.getCustomerInfo();
    managementURL.value = customerInfo?.managementURL ?? null;

    const entitlements = entitlementsFromCustomerInfo(customerInfo);


    // Price (Stripe) and pause state come from our backend — RevenueCat's JS SDK
    // exposes neither a Stripe StoreProduct price nor the Stripe pause window.
    // Both are asked for even with no entitlement, because a pause deliberately
    // lets the subscription lapse: mid-gap RevenueCat has nothing active while
    // Stripe still has a pending restart. Neither query touches Stripe for a
    // user with no Stripe customer, so free users don't pay for this.
    const [priceResult, pauseResult] = await Promise.allSettled([
      graphClient.request<any>(ActiveWebSubscriptionPrice),
      firebaseUserId
        ? graphClient.request<any>(SubscriptionPauseState, { firebaseID: firebaseUserId })
        : Promise.resolve(null),
    ]);

    const priceString = priceStringFromResult(priceResult);
    const pauseState = pauseStateFromResult(pauseResult);

    if (!entitlements.length) {
      // Paused mid-gap or a restart that couldn't charge — otherwise null, and
      // the "no subscription" card renders as before.
      const fallback = buildFallbackDisplayModel(pauseState, { priceString });
      displays.value = fallback ? [fallback] : [];
      flowIndex.value = 0;
      return;
    }

    displays.value = buildDisplayModels(entitlements, {
      isPaused: pauseState.isPaused,
      pauseResumesAt: pauseState.pauseResumesAt,
      pauseAccessEndsAt: pauseState.pauseAccessEndsAt,
      paymentFailed: pauseState.paymentFailed,
      priceString,
    });
    flowIndex.value = 0;
  } catch (error) {
    console.error("Error loading subscription:", error);
    displays.value = [];
    loadFailed.value = true;
    // A resolver that raised a user-facing message (Stripe failures are now
    // classified into one) is worth showing; anything else is transport noise,
    // so fall back to copy that says billing is unaffected.
    const detail = errorMessage(error);
    loadErrorMessage.value = detail === GENERIC_ERROR_MESSAGE ? SUBSCRIPTION_LOAD_FAILURE_MESSAGE : detail;
  } finally {
    loading.value = false;
  }
}

function openPause(index: number) {
  flowIndex.value = index;
  view.value = "pause";
}

function openCancel(index: number) {
  flowIndex.value = index;
  view.value = "cancel";
}

function onUpdatePayment(index: number) {
  const target = displays.value[index] ?? null;
  const action = manageAction(target?.source ?? "other", managementURL.value);
  if (action.kind === "stripe") {
    const url = (window as any).stripeManagementUrl;
    if (url) window.location.href = url;
    return;
  }
  // Sources with no self-serve page (promotional grants, Amazon, …) get a
  // support hand-off rather than a dead "Go" button.
  flowIndex.value = index;
  paymentNeedsSupport.value = action.kind === "support";
  paymentRedirectBody.value = updatePaymentPrompt(action);
}

function confirmPaymentRedirect() {
  const url = storeManagementUrlFor(flowTarget.value);
  if (paymentNeedsSupport.value) {
    contactSupport();
  } else if (url) {
    window.open(url, "_blank", "noopener");
  }
  closePaymentRedirect();
}

function closePaymentRedirect() {
  paymentRedirectBody.value = "";
  paymentNeedsSupport.value = false;
}

function onFlowDone() {
  view.value = "manage";
  load();
}

onMounted(load);
</script>

<style scoped lang="scss">
.purchases__thumb {
  width: 96px;
  height: 96px;
  object-fit: cover;
  flex-shrink: 0;
}

.text-brand-yellow {
  color: #caa300;
}

.purchases__also {
  margin-top: 2.5rem;

  &-hint {
    font-size: 0.95rem;
  }
}
</style>
